<script setup>
import { ref, computed } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue'
import FeaturedCardIcon from '@/Components/Links/FeaturedCardIcon.vue'
import 'vue3-carousel/dist/carousel.css'
import { Carousel, Slide, Navigation } from 'vue3-carousel'
import { buildLinkAssetUrl } from '@/utils/assetUrls.js'

const props = defineProps({
    link: { type: Object, required: true },
    appearance: { type: Object, default: () => ({}) },
    buttonStyle: { type: [Object, Array, String], default: () => ({}) },
    DoBucket: String,
    DoEndpoint: String,
})

const emit = defineEmits(['click', 'share'])

const layout = computed(() => props.link?.options?.layout || 'classic')
const displayMode = computed(() => props.link?.options?.shopify?.display_mode || 'link_only')
const isFeatured = computed(() => layout.value === 'featured')
const isClassic  = computed(() => layout.value === 'classic')
const isCarousel = computed(() => layout.value === 'carousel')
const coverImageUrl = computed(() =>
    buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, props.link?.file),
)

const carouselConfig = {
    itemsToShow: 1.7, // default (mobile)
    wrapAround: true,
    snapAlign: 'center',
    transition: 500,
    breakpoints: {
        768: {
            itemsToShow: 2, // tablet
        },
        1024: {
            itemsToShow: 2.1, // desktop
        },
    },
}


// products provided from backend for profile rendering (see backup.vue)
const products = computed(() => Array.isArray(props.link?.shopifyProducts) ? props.link.shopifyProducts : [])

// helpers for CTA tile condition
const hasThreeProducts = computed(() => displayMode.value === 'products_only' && products.value.length === 3)
const hasShopUrl = computed(() => !!props.link?.url)
const hasAtLeastTwoProducts = computed(() => displayMode.value === 'products_only' && products.value.length >= 2)

// Classic layout expansion state for products_only
const expanded = ref(false)

function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }
function onClick(){ emit('click', props.link) }

function openUrl(e){
    e?.stopPropagation?.()
    const url = props.link?.url
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

function openTarget(e){
    // In classic layout with products_only and available products, toggle slide-down
    if (isClassic.value && displayMode.value === 'products_only' && products.value.length) {
        expanded.value = !expanded.value
        return
    }
    // Otherwise, default behavior: open URL in new tab
    openUrl(e)
}

// Resolve product URL (fallback to link.url or '#')
function productUrl(p){
    return p?.url || p?.product_url || props.link?.url || '#'
}

// Currency price resolver for product tiles (featured + products_only)
function priceOf(p){
    const v = Number(p?.price ?? p?.variants?.[0]?.price)
    return Number.isFinite(v)
        ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: p?.currency || 'GBP' }).format(v)
        : null
}

</script>

<template>
    <!-- FEATURED layout -->
    <GeneralEmbedButton v-if="isFeatured"
                        class="shopify-card"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        :layout="layout"
                        @click="openUrl">
        <!-- Header: icon + centered title + share -->
        <div class="d-flex align-items-center mb-2" style="height:3rem; padding-top:6px; padding-left:12px; padding-right:12px; gap:8px;">
            <FeaturedCardIcon :link="link" platform="calendly" :DoBucket="DoBucket" :DoEndpoint="DoEndpoint" />
            <CardTitle :title="link.title || 'Shopify'" layout="classic" align="center" :max="100" class="flex-grow-1" style="min-width:0; overflow:hidden;" />
            <span class="d-flex align-items-center" aria-label="Share" style="flex-shrink:0">
        <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
        </svg>
      </span>
        </div>

        <!-- Media area: show products when products_only and available; otherwise show cover image (if any) -->
        <div class="text-center mb-3">
            <template v-if="displayMode==='products_only' && hasAtLeastTwoProducts">
                <!-- 2-up edge-to-edge grid -->
                <div class="two-up-grid">
                    <template v-for="(p, idx) in products.slice(0, hasThreeProducts && hasShopUrl ? 3 : 4)" :key="p.id || idx">
                        <a :href="productUrl(p)"
                           target="_blank"
                           rel="noopener noreferrer"
                           @click.stop
                           class="two-up-tile">
                            <img :src="p.image" :alt="p.title || 'Product'" class="two-up-img" />
                            <span v-if="priceOf(p)" class="price-pill">{{ priceOf(p) }}</span>
                        </a>
                    </template>
                    <!-- CTA as 4th tile when exactly 3 products and link url exists -->
                    <a v-if="hasThreeProducts && hasShopUrl"
                       :href="link.url"
                       target="_blank"
                       rel="noopener noreferrer"
                       @click.stop
                       class="two-up-tile cta">
                        <span class="cta-text">Shop all</span>
                    </a>
                </div>
            </template>
            <template v-else>
                <!-- Single product in products_only: show full-width tile -->
                <a v-if="displayMode==='products_only' && products.length === 1"
                   :href="productUrl(products[0])"
                   target="_blank"
                   rel="noopener noreferrer"
                   @click.stop
                   class="two-up-tile"
                   style="width: 100%; height: 200px;">
                    <img :src="products[0].image" :alt="products[0].title || 'Product'" class="two-up-img" />
                    <span v-if="priceOf(products[0])" class="price-pill">{{ priceOf(products[0]) }}</span>
                </a>
                <!-- If featured + link_only and products exist, prefer first product image linking to product -->
                <a v-else-if="displayMode==='link_only' && products.length"
                   :href="productUrl(products[0])"
                   target="_blank"
                   rel="noopener noreferrer"
                   @click.stop
                   class="two-up-tile"
                   style="width: 100%; height: 200px;">
                    <img :src="products[0].image" :alt="products[0].title || 'Product'" class="two-up-img" />
                </a>
                <!-- Otherwise show uploaded cover if present -->
                <div v-else
                     class="mx-auto"
                :style="{
                width: '100%',
                height: '200px',
                borderRadius: '12px',
                backgroundImage: coverImageUrl ? `url(${coverImageUrl})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
             }"
                ></div>
            </template>
        </div>
    </GeneralEmbedButton>

    <!-- CAROUSEL layout -->
    <GeneralEmbedButton v-else-if="isCarousel"
                        :class="['shopify-card', { 'products-only': displayMode==='products_only' }]"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        :layout="layout"
                        @click="openUrl">
        <!-- Header: icon + centered title + share -->
        <div class="d-flex align-items-center mb-2" style="height:3rem; padding-top:6px; padding-left:12px; padding-right:12px; gap:8px;">
            <FeaturedCardIcon :link="link" platform="calendly" :DoBucket="DoBucket" :DoEndpoint="DoEndpoint" />
            <CardTitle :title="link.title || 'Shopify'" layout="classic" align="center" :max="100" class="flex-grow-1" style="min-width:0; overflow:hidden;" />
            <span class="d-flex align-items-center" aria-label="Share" style="flex-shrink:0">
        <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
        </svg>
      </span>
        </div>

        <div class="text-center mb-3" @click.stop>
            <Carousel v-bind="carouselConfig">
                <!-- Use product images as slides when available -->
                <Slide v-if="products.length" v-for="(p, idx) in products" :key="p.id || idx">
                    <a :href="productUrl(p)" target="_blank" rel="noopener noreferrer" class="two-up-tile" style="aspect-ratio:auto; width:100%; height:200px;">
                        <img :src="p.image" :alt="p.title || 'Product'" class="two-up-img" />
                        <span v-if="priceOf(p)" class="price-pill">{{ priceOf(p) }}</span>
                    </a>
                </Slide>

                <!-- Fallback slides when no products -->
                <Slide v-else v-for="slide in 3" :key="slide">
                    <div class="carousel__item two-up-tile" style="height:200px;">{{ slide }}</div>
                </Slide>

                <template #addons>
                    <Navigation />
                </template>
            </Carousel>
        </div>
    </GeneralEmbedButton>

    <!-- CLASSIC layout -->
    <GeneralButton v-else-if="isClassic"
                   class="shopify-card"
                   :link="link"
                   :appearance="appearance"
                   :buttonStyle="buttonStyle"
                   :DoBucket="DoBucket"
                   :DoEndpoint="DoEndpoint"
                   :layout="layout"
                   :hideLeftImage="displayMode==='products_only' && expanded"
                   @click="openTarget">
        <template #title>
            <CardTitle :title="link.title || 'Shopify'" layout="classic" align="center" :max="100" />
        </template>
        <template #actions>
            <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                      d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
            </svg>
        </template>

        <!-- Slide-down products area for products_only (classic), using the same 2-up grid structure as featured -->
        <transition name="slide-fade">
            <div v-if="displayMode==='products_only' && expanded" class="w-100 mt-2" @click.stop>
                <!-- Show grid only if at least 2 products -->
                <div v-if="hasAtLeastTwoProducts" class="two-up-grid">
                    <template v-for="(p, idx) in products.slice(0, hasThreeProducts && hasShopUrl ? 3 : 4)" :key="p.id || idx">
                        <a :href="productUrl(p)"
                           target="_blank"
                           rel="noopener noreferrer"
                           @click.stop
                           class="two-up-tile">
                            <img :src="p.image" :alt="p.title || 'Product'" class="two-up-img" />
                            <span v-if="priceOf(p)" class="price-pill">{{ priceOf(p) }}</span>
                        </a>
                    </template>
                    <!-- CTA as 4th tile when exactly 3 products and link url exists -->
                    <a v-if="hasThreeProducts && hasShopUrl"
                       :href="link.url"
                       target="_blank"
                       rel="noopener noreferrer"
                       @click.stop
                       class="two-up-tile cta">
                        <span class="cta-text">Shop all</span>
                    </a>
                </div>
                <!-- Single product: full-width tile -->
                <a v-else-if="products.length === 1"
                   :href="productUrl(products[0])"
                   target="_blank"
                   rel="noopener noreferrer"
                   @click.stop
                   class="two-up-tile"
                   style="width: 100%; height: 200px;">
                    <img :src="products[0].image" :alt="products[0].title || 'Product'" class="two-up-img" />
                    <span v-if="priceOf(products[0])" class="price-pill">{{ priceOf(products[0]) }}</span>
                </a>
            </div>
        </transition>
    </GeneralButton>
</template>

<style scoped>

.shopify-card :deep(iframe){ border:0; }

/* 2-up edge-to-edge grid for featured products_only */
.two-up-grid{
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    width: 100%;
}
.two-up-tile{
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 1 / 1; /* square tiles keep consistent rows */
    overflow: hidden;
    background: #f5f5f5;
    text-decoration: none;
    position: relative; /* allow absolute-positioned price pill */
}
.two-up-img{
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.two-up-tile.cta{
    background: #eef6ff;
}
.cta-text{
    font-weight: 600;
    font-size: 14px;
    color: inherit;
}

/* Price pill overlay inside product tiles (featured + products_only) */
.price-pill{
    position: absolute;
    right: 8px;
    bottom: 8px;
    padding: 4px 8px;
    border-radius: 12px;
    background: rgba(0,0,0,.55);
    color: #fff;
    font-weight: 700;
    font-size: 12px;
    backdrop-filter: blur(6px);
}

:root {
    --carousel-transition: 300ms;
}

.shopify-card :deep(.carousel) {
    position: relative;
}

/* Center nav buttons */
.shopify-card :deep(.carousel__prev),
.shopify-card :deep(.carousel__next) {
    position: absolute;
    top: 50% !important;
    transform: translateY(-50%);
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    border: 4px solid #fff;
    background: rgba(0,0,0,.35);
    color: #fff;
    box-shadow: 0 8px 24px rgba(0,0,0,.18);
    backdrop-filter: blur(3px);
}

.shopify-card :deep(.carousel__prev) { left: -10px; }
.shopify-card :deep(.carousel__next) { right: -10px; }

.shopify-card :deep(.carousel__icon) {
    width: 28px;
    height: 28px;
}

/* Slide down transition (mirrors Twitch card) */
.slide-fade-enter-from,.slide-fade-leave-to{ max-height:0; opacity:0; transform:translateY(-6px); }
.slide-fade-enter-active,.slide-fade-leave-active{ transition:all .25s ease; }
.slide-fade-enter-to,.slide-fade-leave-from{ max-height:1000px; opacity:1; transform:translateY(0); }

/* Add gap between products in carousel when display mode is products_only */
.shopify-card.products-only :deep(.carousel__slide) {
    padding-left: 6px;
    padding-right: 6px;
}

/* Smaller on mobile */
@media (max-width: 768px) {
    .shopify-card :deep(.carousel__prev),
    .shopify-card :deep(.carousel__next) {
        width: 32px;
        height: 32px;
        border-width: 3px;
    }
    .shopify-card :deep(.carousel__icon) {
        width: 22px;
        height: 22px;
    }
}


</style>
